import React, { useState, useEffect } from 'react';
import { scaledDrawerWidth } from '../theme';
import {
    Drawer,
    Box,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
    Divider,
    Avatar,
    Chip,
    Button,
    useTheme,
    useMediaQuery,
    Collapse,
    IconButton,
    Toolbar
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Logo from './Logo';
import {
    Folder,
    Business,
    Description,
    Science,
    BarChart,
    AccessTime,
    Assignment,
    People,
    Lock,
    Security,
    ExpandLess,
    ExpandMore,
    Logout,
    Person,
    Computer,
    CreateNewFolder,
    Update,
    Shield,
    MoreTime,
    Analytics,
    Store,
    Money,
    CloudQueue,
    HomeRepairService,
    Book,
    ViewKanban,
    AssignmentInd,
    Settings,
    StarOutline,
    SmartToy,
    EmojiEvents,
    SportsSoccer,
    PictureAsPdf,
    FolderShared,
    HelpOutline
} from '@mui/icons-material';
import { localStore } from '../utils/localStore';

const SIDEBAR_EXPANDED_CATEGORIES_KEY = 'sidebar_expanded_categories_v1';

interface MenuItem {
    title: string;
    path: string;
    featureKey: string;
    icon: React.ReactNode;
    public?: boolean;
}

interface MenuCategory {
    title: string;
    icon: React.ReactNode;
    items: MenuItem[];
}

interface HamburgerMenuProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
}

const HamburgerMenu: React.FC<HamburgerMenuProps> = ({ isOpen, setIsOpen }) => {
    const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md')); // Match sidebar breakpoint

    useEffect(() => {
        let cancelled = false;

        void localStore.get(SIDEBAR_EXPANDED_CATEGORIES_KEY).then((saved) => {
            if (cancelled) {
                return;
            }
            if (Array.isArray(saved) && saved.every((item) => typeof item === 'string')) {
                setExpandedCategories(saved);
            }
        });

        return () => {
            cancelled = true;
        };
    }, []);

    // Define menu categories with icons
    const menuCategories: MenuCategory[] = [
        {
            title: 'PMO',
            icon: <Business />,
            items: [
                {
                    title: 'Project List',
                    path: '/projects',
                    featureKey: 'projects',
                    icon: <Folder fontSize="small" />
                },
                {
                    title: 'Client Feedback',
                    path: '/pmo/client-feedback',
                    featureKey: 'client_feedback',
                    icon: <StarOutline fontSize="small" />
                },
                {
                    title: 'Create Project',
                    path: '/projects/create',
                    featureKey: 'project_posts',
                    icon: <CreateNewFolder fontSize="small" />
                },
                {
                    title: 'Project Permissions',
                    path: '/permissions',
                    featureKey: 'permissions',
                    icon: <Shield fontSize="small" />
                },
                {
                    title: 'DCC Terms Configuration',
                    path: '/pmo/revisions-config',
                    featureKey: 'project_posts',
                    icon: <Settings fontSize="small" />
                }
            ]
        },
        {
            title: 'Laboratory',
            icon: <Science />,
            items: [
                {
                    title: 'LCM Lab',
                    path: '/lcm-lab',
                    featureKey: 'lcm_lab',
                    icon: <Science fontSize="small" />
                },
                {
                    title: 'Results',
                    path: '/results',
                    featureKey: 'results',
                    icon: <BarChart fontSize="small" />
                }
            ]
        },
        {
            title: 'IT Management',
            icon: <Computer />,
            items: [
                {
                    title: 'User Management',
                    path: '/users',
                    featureKey: 'user_management',
                    icon: <Person fontSize="small" />
                },
                {
                    title: 'Feature Access',
                    path: '/feature-permissions',
                    featureKey: 'feature_access',
                    icon: <Security fontSize="small" />
                },
                {
                    title: 'Third Parties',
                    path: '/third-parties',
                    featureKey: 'third_parties',
                    icon: <People fontSize="small" />
                },
                {
                    title: 'General Config',
                    path: '/general-config',
                    featureKey: 'general_config',
                    icon: <Settings fontSize="small" />
                }
            ]
        },
        {
            title: 'HR',
            icon: <People />,
            items: [
                {
                    title: 'Timesheet',
                    path: '/timesheet',
                    featureKey: 'timesheet',
                    icon: <MoreTime fontSize="small" />
                },
                {
                    title: 'Calendar',
                    path: '/time-off',
                    featureKey: 'time_management',
                    icon: <AccessTime fontSize="small" />
                },
                {
                    title: 'HR Management',
                    path: '/hr/management',
                    featureKey: 'hr',
                    icon: <Assignment fontSize="small" />
                },
                {
                    title: 'CV Bank',
                    path: '/hr/cv-bank',
                    featureKey: 'cv_bank',
                    icon: <AssignmentInd fontSize="small" />
                },
                {
                    title: 'My Documents',
                    path: '/hr/documents',
                    featureKey: 'my_documents',
                    icon: <Folder fontSize="small" />
                }
            ]
        },

        {
            title: 'Expenses',
            icon: <Money />,
            items: [
                {
                    title: 'Expenses',
                    path: '/hr/expenses',
                    featureKey: 'expenses',
                    icon: <Money fontSize="small" />
                }
            ]
        },
        {
            title: 'Sales',
            icon: <Analytics />,
            items: [
                {
                    title: 'Commercial Dashboard',
                    path: '/sales/dashboard',
                    featureKey: 'analytics',
                    icon: <BarChart fontSize="small" />
                },
                {
                    title: 'Leads',
                    path: '/sales/leads',
                    featureKey: 'analytics',
                    icon: <Assignment fontSize="small" />
                },
                {
                    title: 'Proposal',
                    path: '/sales/proposals',
                    featureKey: 'analytics',
                    icon: <Description fontSize="small" />
                },
                {
                    title: 'Contact List',
                    path: '/clients',
                    featureKey: 'client_management',
                    icon: <Store fontSize="small" />
                },
                {
                    title: 'Capacity Snapshot',
                    path: '/sales/capacity',
                    featureKey: 'analytics',
                    icon: <Analytics fontSize="small" />
                }
            ]
        },
        {
            title: 'Other Tools',
            icon: <HomeRepairService />,
            items: [
                {
                    title: 'Plane',
                    path: 'https://plane.it.nautilusdynamics.com/',
                    featureKey: 'plane',
                    icon: <ViewKanban fontSize="small" />
                },
                {
                    title: 'Library',
                    path: 'https://paperless.nautilusdynamics.com/',
                    featureKey: 'library',
                    icon: <Book fontSize="small" />
                },
                {
                    title: 'AI Assistant',
                    path: '/ai-chat',
                    featureKey: 'ai_assistant',
                    icon: <SmartToy fontSize="small" />
                },
                {
                    title: 'PDF Editor',
                    path: '/pdf-editor',
                    featureKey: 'pdf_editor',
                    icon: <PictureAsPdf fontSize="small" />
                },
                {
                    title: 'CFOS',
                    path: '/cfos',
                    featureKey: 'cfos',
                    icon: <FolderShared fontSize="small" />
                },
                {
                    title: 'Help',
                    path: '/help',
                    featureKey: 'help',
                    icon: <HelpOutline fontSize="small" />
                }
            ]
        },
        {
            title: 'Entertainment',
            icon: <EmojiEvents />,
            items: [
                {
                    title: 'WC2026',
                    path: '/wc2026',
                    featureKey: 'wc2026',
                    icon: <SportsSoccer fontSize="small" />
                }
            ]
        }
    ];

    // Filter categories to only show items user has access to
    const availableCategories = menuCategories
        .map(category => ({
            ...category,
            items: category.items.filter(item =>
                item.public || user?.allowed_features?.includes(item.featureKey)
            )
        }))
        .filter(category => category.items.length > 0);

    const toggleCategory = (categoryTitle: string) => {
        setExpandedCategories((prev) => {
            const next = prev.includes(categoryTitle)
                ? prev.filter((c) => c !== categoryTitle)
                : [...prev, categoryTitle];
            void localStore.set(SIDEBAR_EXPANDED_CATEGORIES_KEY, next);
            return next;
        });
    };

    const handleNavigation = (path: string) => {
        if (path.startsWith('http')) {
            window.open(path, '_blank', 'noopener,noreferrer');
        } else {
            navigate(path);
        }

        // On mobile, close sidebar after navigation. On desktop, keep it open.
        if (isMobile) {
            setIsOpen(false);
        }
    };

    const handleLogout = () => {
        logout();
        setIsOpen(false);
    };

    const drawerWidth = isMobile ? '85vw' : scaledDrawerWidth;

    // Design System: Nautilus Console
    const consoleColors = {
        deepOcean: '#0f2134',
        sand: '#ccba9b',
        panelBorder: theme.palette.mode === 'dark' ? 'rgba(204, 186, 155, 0.15)' : 'rgba(15, 33, 52, 0.12)',
        activeBg: theme.palette.mode === 'dark' ? 'rgba(204, 186, 155, 0.08)' : 'rgba(15, 33, 52, 0.05)',
        hoverBg: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
    };

    return (
        <>
            {/* Drawer Menu */}
            <Drawer
                variant={isMobile ? "temporary" : "persistent"}
                anchor="left"
                open={isOpen}
                onClose={() => setIsOpen(false)}
                sx={{
                    width: isOpen ? drawerWidth : 0,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        backgroundColor: theme.palette.mode === 'dark' ? '#0a1622' : 'background.paper', // Abyssal in dark
                        backgroundImage: 'none',
                        borderRight: `1px solid ${consoleColors.panelBorder}`,
                        boxSizing: 'border-box',
                    }
                }}
            >
                <Box
                    sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden'
                    }}
                >
                    {/* Spacer for Persistent Drawer (Desktop) to clear AppBar */}
                    {!isMobile && <Toolbar />}

                    {/* Navigation Categories */}
                    <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', py: 2 }}>
                        <List disablePadding>
                            {availableCategories.map((category) => {
                                const isExpanded = expandedCategories.includes(category.title);
                                return (
                                    <React.Fragment key={category.title}>
                                        <ListItem disablePadding sx={{ display: 'block', mb: 0.5 }}>
                                            <ListItemButton
                                                onClick={() => toggleCategory(category.title)}
                                                sx={{
                                                    py: 1,
                                                    mx: 1.5,
                                                    borderRadius: 1,
                                                    border: '1px solid transparent',
                                                    '&:hover': {
                                                        backgroundColor: consoleColors.hoverBg,
                                                        borderColor: consoleColors.panelBorder,
                                                    }
                                                }}
                                            >
                                                <ListItemIcon sx={{ minWidth: 36, color: 'text.secondary' }}>
                                                    {category.icon}
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={category.title}
                                                    primaryTypographyProps={{
                                                        variant: 'overline', // Technical feel
                                                        fontWeight: 700,
                                                        letterSpacing: '0.05em',
                                                        color: 'text.secondary',
                                                        sx: { lineHeight: 1.2 }
                                                    }}
                                                />
                                                {isExpanded ? <ExpandLess fontSize="small" sx={{ color: 'text.disabled' }} /> : <ExpandMore fontSize="small" sx={{ color: 'text.disabled' }} />}
                                            </ListItemButton>
                                        </ListItem>
                                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                            <List component="div" disablePadding>
                                                {category.items.map((item) => {
                                                    const isActive = location.pathname === item.path;
                                                    return (
                                                        <ListItem key={item.path} disablePadding>
                                                            <ListItemButton
                                                                onClick={() => handleNavigation(item.path)}
                                                                selected={isActive}
                                                                sx={{
                                                                    pl: 2.5, // Indent
                                                                    py: 0.75,
                                                                    mx: 1.5,
                                                                    borderRadius: 1,
                                                                    position: 'relative',
                                                                    overflow: 'hidden',
                                                                    transition: 'all 0.2s ease',
                                                                    // Active State Style ("Lit Up")
                                                                    '&.Mui-selected': {
                                                                        backgroundColor: consoleColors.activeBg,
                                                                        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(204, 186, 155, 0.3)' : 'rgba(15, 33, 52, 0.1)'}`,
                                                                        '&:hover': {
                                                                            backgroundColor: consoleColors.activeBg,
                                                                        },
                                                                        '&::before': {
                                                                            content: '""',
                                                                            position: 'absolute',
                                                                            left: 0,
                                                                            top: 0,
                                                                            bottom: 0,
                                                                            width: '3px',
                                                                            backgroundColor: consoleColors.sand, // Brand Sand Accent
                                                                        }
                                                                    },
                                                                    '&:hover': {
                                                                        backgroundColor: consoleColors.hoverBg,
                                                                    }
                                                                }}
                                                            >
                                                                <ListItemIcon sx={{
                                                                    minWidth: 32,
                                                                    color: isActive ? consoleColors.sand : 'text.disabled',
                                                                    transition: 'color 0.2s'
                                                                }}>
                                                                    {item.icon}
                                                                </ListItemIcon>
                                                                <ListItemText
                                                                    primary={item.title}
                                                                    primaryTypographyProps={{
                                                                        fontSize: '0.85rem',
                                                                        fontWeight: isActive ? 600 : 400,
                                                                        color: isActive ? 'text.primary' : 'text.secondary',
                                                                        fontFamily: isActive ? 'Roboto, sans-serif' : 'inherit'
                                                                    }}
                                                                />
                                                            </ListItemButton>
                                                        </ListItem>
                                                    );
                                                })}
                                            </List>
                                        </Collapse>
                                        {/* Technical Divider for sections */}
                                        <Box sx={{ mx: 2, my: 1, borderBottom: `1px dashed ${consoleColors.panelBorder}`, opacity: 0.5 }} />
                                    </React.Fragment>
                                );
                            })}
                        </List>
                    </Box>

                    {/* Logout Section */}

                    <Box sx={{ p: 2, borderTop: `1px solid ${consoleColors.panelBorder}` }}>
                        <Button
                            fullWidth
                            variant="outlined"
                            color="error" // Keep error for logout, but style it to match system
                            startIcon={<Logout />}
                            onClick={handleLogout}
                            sx={{
                                py: 0.75,
                                fontWeight: 500,
                                fontSize: '0.8125rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 99, 99, 0.3)' : undefined,
                                background: theme.palette.mode === 'dark' ? 'rgba(60, 20, 20, 0.3)' : undefined,
                            }}
                        >
                            Log Out
                        </Button>
                    </Box>
                </Box>
            </Drawer>
        </>
    );
};

export default HamburgerMenu;
